<template>
  <q-layout
    :id="$q.screen.lt.md ? 'consumer-layout-mobile' : 'consumer-layout'"
    :view="$q.screen.lt.md ? 'hHh lpR fFf' : 'LHh lpR lff'"
  >
    <q-drawer
      v-if="showStyleDrawer"
      :model-value="true"
      side="left"
      bordered
    >
      <model-form
        v-if="headerForStyle"
        ref="modelForm"
        :model="currentSite"
        hide-title
        hide-header-buttons
        :is-dialog="false"
      >
        <consumer-header-style-editor
          :current-site="currentSite"
          show-site-selector-configuration
        >
          <template #action-footer>
            <div class="row">
              <q-btn-group
                class="full-width"
                spread
              >
                <q-btn
                  label="Save"
                  color="primary"
                  :text-color="$getColor(currentSite, 'primary')"
                  @click="saveHeaderStyle()"
                />
                <q-btn
                  label="Cancel"
                  color="negative"
                  :text-color="$getColor(currentSite, 'negative')"
                  @click="showStyleDrawer = false; headerForStyle = false"
                />
              </q-btn-group>
            </div>
          </template>
        </consumer-header-style-editor>
      </model-form>
      <style-drawer
        v-else
        :page="pageForStyle"
        :section="sectionForStyle"
        @close="showStyleDrawer = false"
        @handle-page-change="handlePageChange && handlePageChange()"
      />
    </q-drawer>

    <consumer-header
      @edit-pages="editPages = true"
      @edit-navigations="editNavigations = true"
      @edit-site-css="editCss"
      @time-travel="$refs.timeTravelWidget.startTimeTravel()"
      @search="showSearch = true"
      @edit-header-style="editHeaderStyle"
      @show-site-selector-drawer="showSiteSelectorDialog(false)"
    />
    <q-page-container
      :style="[currentSite.consumerConfig.headerBgTransparent ? { 'padding-top': '0' } : {}]"
    >
      <router-view
        v-slot="{ Component }"
        :key="$route.fullPath"
      >
        <keep-alive
          exclude="Orders"
          max="5"
        >
          <component
            :is="Component"
            @time-travel="$refs.timeTravelWidget.startTimeTravel()"
            @stop-time-travel="$refs.timeTravelWidget.stopTimeTravel()"
            @edit-css="(page, section, handleChange) => editCss(page, section, handleChange)"
            @stop-edit-css="showStyleDrawer = false"
            @show-site-selector-drawer="showSiteSelectorDialog(true)"
          />
        </keep-alive>
      </router-view>
    </q-page-container>
    <mobile-footer />
    <q-dialog
      v-model="editPages"
      full-width
    >
      <page-table />
    </q-dialog>
    <q-dialog
      v-model="editNavigations"
      full-width
    >
      <navigation-table />
    </q-dialog>
    <drawer-dialog
      ref="siteSelectorDrawerDialog"
    >
      <template #content>
        <site-selector-with-zones
          class="bg-transparent"
          :class="$q.screen.gt.xs ? 'q-mx-md' : 'q-mx-sm'"
          :redirect-home-page="redirectHomePage"
          @select="hideSiteSelectorDialog"
        />
      </template>
    </drawer-dialog>
    <time-travel-widget
      ref="timeTravelWidget"
    />
    <consumer-search
      v-model="showSearch"
    />
  </q-layout>
</template>

<script>
import { Cookies, Dark, Notify } from 'quasar'
import { HEADER_TYPES } from 'src/models/site/Site'
import ConsumerHeader from 'src/modules/consumer/layouts/ConsumerHeader'
import MobileFooter from 'src/modules/consumer/layouts/MobileFooter'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'ConsumerLayout',
  components: {
    ConsumerHeader,
    ConsumerSearch: defineAsyncComponent(() => import('src/modules/admin/search/ConsumerSearch')),
    MobileFooter,
    ModelForm: defineAsyncComponent(() => import('src/modules/admin/components/ModelForm/ModelForm')),
    NavigationTable: defineAsyncComponent(() => import('src/modules/admin/navigation/NavigationTable')),
    PageTable: defineAsyncComponent(() => import('src/modules/admin/page/PageTable')),
    StyleDrawer: defineAsyncComponent(() => import('src/modules/consumer/components/StyleDrawer')),
    TimeTravelWidget: defineAsyncComponent(() => import('src/modules/consumer/layouts/TimeTravelWidget')),
    ConsumerHeaderStyleEditor: defineAsyncComponent(() => import('src/modules/consumer/components/ConsumerHeaderStyleEditor')),
    SiteSelectorWithZones: defineAsyncComponent(() => import('src/modules/consumer/components/site-selector/SiteSelectorWithZones')),
    DrawerDialog: defineAsyncComponent(() => import('src/modules/consumer/components/DrawerDialog'))
  },
  data () {
    return {
      editPages: false,
      editNavigations: false,

      showStyleDrawer: false,
      pageForStyle: null,
      sectionForStyle: null,

      headerForStyle: false,

      showSearch: false,

      redirectHomePage: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'publishedNewsItems', 'navigations'],

  computed: {
    footerNavigations () {
      return _.filter(this.navigations?.data, navigation => navigation.showInFooter && !navigation.isArchived)
    },
    unseenNewsCount () {
      if (this.lastViewedNewsAt) {
        return _.filter(this.publishedNewsItems.data, newsItem => newsItem.publishedAt > this.lastViewedNewsAt).length
      } else {
        return this.publishedNewsItems.data.length
      }
    }
  },

  beforeMount () {
    const CORDOVA_KEY = 'cordovaNative'

    if (Cookies.has(CORDOVA_KEY)) {
      window._cordovaNative = true
    } else if (window._cordovaNative) {
      !Cookies.has(CORDOVA_KEY) && Cookies.set(CORDOVA_KEY, true, { expires: 365 })
    }
  },

  mounted () {
    this.requestTrackingPermissions()
  },

  created () {
    _.extend(this, {
      HEADER_TYPES
    })
    this.setThemeAndStyle()
  },

  unmounted () {
    document.removeEventListener('deviceready', this.showIosTrackingNotification)
  },

  methods: {
    requestTrackingPermissions () {
      if (window._cordovaNative) {
        if (window.cordova) {
          this.showIosTrackingNotification()
        } else {
          document.addEventListener('deviceready', this.showIosTrackingNotification)

          // Add a timeout to show the tracking notification prompt for redundancy (in case
          // there is an issue with the event listener). The prompt will only be shown once
          // even if we call this method multiple times.
          setTimeout(this.showIosTrackingNotification, 1000)
        }
      } else {
        if (this.$route.query.hide_cookie_prompt === 'true') {
          localStorage.acceptedCookieNotification = true
        }

        this.showCookieNotification()
      }
    },
    runIosCommand (service, action, args) {
      return new Promise((resolve, reject) => {
        window.cordova.exec(resolve, reject, service, action, args)
      })
    },
    async showIosTrackingNotification () {
      if (!window.cordova) return

      try {
        // Determine if the iOS user has already allowed tracking
        const isTrackingAvailable = await this.runIosCommand('ImpacTracking', 'trackingAvailable', [])
        if (isTrackingAvailable) {
          window._isIosTrackingAllowed = true
          return
        }

        // If not, determine if we can request tracking permissions
        const canRequestTracking = await this.runIosCommand('ImpacTracking', 'canRequestTracking', [])
        if (!canRequestTracking) {
          return
        }

        const didAcceptTrackingRequest = await this.runIosCommand('ImpacTracking', 'requestTracking', [])
        window._isIosTrackingAllowed = !!didAcceptTrackingRequest
      } catch (error) {
        this.$rollbar.error(`Failed to prompt for tracking on iOS: ${error.message}`)
      }
    },
    showCookieNotification () {
      if (localStorage.acceptedCookieNotification) return

      Notify.create({
        message: 'This website uses cookies. For more information, see our <a href="/cookie-policy">Cookie Policy</a>',
        html: true,
        color: 'grey',
        multiLine: true,
        timeout: 0,
        actions: [
          {
            color: 'yellow',
            label: 'Accept & Dismiss',
            handler: () => {
              localStorage.acceptedCookieNotification = true
            }
          }
        ]
      })
    },
    setThemeAndStyle () {
      if (!this.currentSite) return

      this.$mh.setThemeColors(this.currentSite)

      this.$mh.setSiteStyles(this.currentSite.transformedStyles)

      // For Dark Mode toggle
      if (this.$consumer) {
        Dark.set(this.currentSite.consumerConfig.darkMode)
      }
    },
    editCss (page, section, handleChange) {
      this.pageForStyle = page
      this.sectionForStyle = section
      this.handlePageChange = handleChange

      this.showStyleDrawer = true
    },
    editHeaderStyle () {
      this.headerForStyle = true
      this.showStyleDrawer = true
    },
    async saveHeaderStyle () {
      this.headerForStyle = false
      this.showStyleDrawer = false
      await this.currentSite.save()
    },
    showSiteSelectorDialog (keepQueryParams) {
      this.$refs.siteSelectorDrawerDialog.show()
      this.redirectHomePage = !keepQueryParams
    },
    hideSiteSelectorDialog () {
      this.$refs.siteSelectorDrawerDialog.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
  .nav-link {
    display: block;
    color: #FFF;
    text-decoration: none;
  }
</style>

<style lang="scss">
  #consumer-layout-mobile {
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
  }
</style>
